import React from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** 테스트 주입용 — 기본값은 window.location.reload */
  onReload?: () => void;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[ErrorBoundary] 콕핏 렌더링 오류:", error, info.componentStack);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
    // 페이지 새로고침으로 완전한 상태를 복원한다.
    if (this.props.onReload) {
      this.props.onReload();
    } else {
      window.location.reload();
    }
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        style={{
          display: "grid",
          placeItems: "center",
          minHeight: "100vh",
          background: "var(--aw-bg, #f7f8fa)",
          font: "400 14px var(--aw-font, system-ui)",
          color: "var(--aw-text, #15171c)",
          padding: "32px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: "100%",
            background: "var(--aw-surface, #fff)",
            borderRadius: "var(--aw-r, 13px)",
            boxShadow: "var(--aw-sh-2, 0 2px 6px rgba(20,33,52,.05),0 8px 22px rgba(20,33,52,.07))",
            padding: "32px 28px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              margin: "0 0 8px",
              fontSize: 15,
              fontWeight: 700,
              color: "var(--aw-err-text, #b42318)",
            }}
          >
            콕핏에 오류가 발생했습니다
          </p>
          <p
            style={{
              margin: "0 0 24px",
              fontSize: 13,
              color: "var(--aw-text-2, #646b76)",
              wordBreak: "break-all",
            }}
          >
            {this.state.error?.message ?? "알 수 없는 오류"}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            style={{
              padding: "9px 24px",
              borderRadius: "var(--aw-r-btn, 11px)",
              border: "none",
              background: "var(--aw-navy, #2f5d8c)",
              color: "#fff",
              font: "600 14px var(--aw-font, system-ui)",
              cursor: "pointer",
            }}
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }
}
